'use client';
import { type ReactNode, useState } from 'react';

import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Button from './Button';

type Props = {
  cancelHandler: () => void;
  confirmHandler: () => void;
  title: string;
  message: string;
  actionButtonText: string;
  // Colours the action button. Replaced a raw class override, which could
  // never win: Button already sets a background from its variant, and CSS
  // source order picked it over the override — so every destructive confirm
  // in the app rendered primary-purple.
  variant?: 'PRIMARY' | 'DANGER' | 'WARNING';
  actionButtonLoading?: boolean;
  // Type-to-confirm gate for dangerous / irreversible actions: when set, the
  // action button stays disabled until the user types this exact phrase.
  confirmPhrase?: string;
  /** Detail under the message, for a choice that needs more than a line to
   *  explain (what sharing a runner hands over, say). */
  children?: ReactNode;
};

const Confirmation = ({
  cancelHandler,
  confirmHandler,
  title,
  message,
  variant = 'PRIMARY',
  actionButtonText,
  actionButtonLoading,
  confirmPhrase,
  children
}: Props) => {
  const [typed, setTyped] = useState('');
  const phraseSatisfied = !confirmPhrase || typed.trim() === confirmPhrase;

  return (
    <Modal closeHandler={cancelHandler}>
      <ModalHeader closeHandler={cancelHandler}>
        <h2>{title}</h2>
      </ModalHeader>
      <ModalContent>
        <p className="my-4 text-base text-textPrimary">{message}</p>
        {children && <div className="mb-4">{children}</div>}
        {confirmPhrase && (
          <div className="mb-4">
            <label className="mb-1.5 block text-sm text-textSecondary">
              Type{' '}
              <span className="font-emphasis text-destructive">
                {confirmPhrase}
              </span>{' '}
              to confirm.
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={confirmPhrase}
              className="w-full rounded-lg border border-ds-border-standard bg-background-primary px-3 py-2 text-base text-textPrimary placeholder:text-textSecondary/50 focus:border-primary/60 focus:outline-none"
            />
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <Button
          text="Cancel"
          variant="SECONDARY"
          classNames="w-28"
          borderTransparent={false}
          onClick={cancelHandler}
        />
        <Button
          text={actionButtonText}
          variant={variant}
          classNames="w-fit ml-2"
          onClick={confirmHandler}
          loading={actionButtonLoading}
          disabled={actionButtonLoading || !phraseSatisfied}
        />
      </ModalFooter>
    </Modal>
  );
};

export default Confirmation;
